'use client';

import { useQuery, useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type { ScoredTrend, TrendFilters } from './use-trends';

interface BookmarkIdsResponse {
  ids: number[];
}

const PAGE_SIZE = 50;

function buildFilterParams(filters: TrendFilters): string {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.source) params.set('source', filters.source);
  if (filters.min_score != null) params.set('min_score', String(filters.min_score));
  if (filters.max_score != null) params.set('max_score', String(filters.max_score));
  if (filters.language) params.set('language', filters.language);
  if (filters.category) params.set('category', filters.category);
  if (filters.date_from) params.set('date_from', filters.date_from);
  if (filters.date_to) params.set('date_to', filters.date_to);
  return params.toString();
}

export function useBookmarkIds() {
  return useQuery<Set<number>>({
    queryKey: ['bookmark-ids'],
    queryFn: async () => {
      const data = await apiFetch<BookmarkIdsResponse>('/bookmarks/ids');
      return new Set(data.ids);
    },
    staleTime: 30_000,
  });
}

export function useToggleBookmark() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ trendId, isBookmarked }: { trendId: number; isBookmarked: boolean }) => {
      if (isBookmarked) {
        await apiFetch(`/bookmarks/${trendId}`, { method: 'DELETE' });
      } else {
        await apiFetch(`/bookmarks/${trendId}`, { method: 'POST' });
      }
      return { trendId, isBookmarked };
    },
    onMutate: async ({ trendId, isBookmarked }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['bookmark-ids'] });

      // Snapshot previous value
      const previousIds = queryClient.getQueryData<Set<number>>(['bookmark-ids']);

      // Optimistically update
      queryClient.setQueryData<Set<number>>(['bookmark-ids'], (old) => {
        const next = new Set(old);
        if (isBookmarked) {
          next.delete(trendId);
        } else {
          next.add(trendId);
        }
        return next;
      });

      return { previousIds };
    },
    onError: (_err, _vars, context) => {
      // Rollback on error
      if (context?.previousIds) {
        queryClient.setQueryData(['bookmark-ids'], context.previousIds);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['bookmark-ids'] });
      queryClient.invalidateQueries({ queryKey: ['bookmarked-trends'] });
    },
  });
}

export function useBookmarkedTrends(filters: TrendFilters) {
  return useInfiniteQuery<ScoredTrend[]>({
    queryKey: ['bookmarked-trends', filters],
    queryFn: async ({ pageParam }) => {
      const filterStr = buildFilterParams(filters);
      const sep = filterStr ? '&' : '';
      return apiFetch<ScoredTrend[]>(
        `/bookmarks?${filterStr}${sep}limit=${PAGE_SIZE}&offset=${pageParam}`
      );
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      return allPages.flat().length;
    },
    initialPageParam: 0,
  });
}
